import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Badge, Btn, Overline } from '../ForBrands/ui/BrandUI';
import Icon from '../ForBrands/ui/icons';
import '../ForBrands/brandMode.css';

const NO_PERMISSIONS = [];

const LockIcon = () => <Icon name="lock" size={14} strokeWidth={2} />;

// Where Upgrade goes: the account's billing page inside its own shell.
// Coaches in Coach Mode, pure brands (not admins or Cache AI staff) in Brand
// Mode, recruiters, AD/PD and staff brands in the Player Index, athletes in
// Athlete Mode. Null for an account with no billing page (reps, supporters,
// interns, staff without a buying role): the paywall then shows a note
// instead of an Upgrade button that would dead-end.
export const upgradePathFor = (permissions = []) => {
  const has = (p) => (permissions || []).includes(p);
  if (has('IS_COACH')) return '/coach/billing';
  if (has('IS_BRAND_AGENCY') && !has('IS_ADMIN') && !has('IS_CACHE_AI')) return '/brand/billing';
  if (has('IS_BRAND_AGENCY') || has('IS_RECRUITER_SCOUT') || has('IS_AD_PD')) return '/business/billing';
  if (has('IS_ATHLETE')) return '/athlete/billing';
  return null;
};

// Shown in place of Upgrade to an account with no billing page of its own.
export const PREMIUM_NOTE = 'Premium sections open for subscribed accounts.';

// The caller picks the background: the compact card is already bm-subtle.
const PremiumNote = ({ className = 'bg-bm-subtle' }) => (
  <p className={`flex items-center gap-2 rounded-[8px] px-4 py-3 bm-body-s text-bm-muted ${className}`}>
    <span className="inline-flex shrink-0 text-bm-orange">
      <LockIcon />
    </span>
    {PREMIUM_NOTE}
  </p>
);

/**
 * Paywall (V2 style). Renders the children when the viewer's entitlement is
 * one of the allowed tiers; otherwise a locked card with an upgrade CTA, or
 * a short note for an account with no billing page to upgrade on.
 *
 * Usage:
 * <Paywall requiredEntitlement="athlete_premium" title="Premium Feature">
 *   <YourPremiumContent />
 * </Paywall>
 *
 * Or with multiple allowed entitlements:
 * <Paywall requiredEntitlements={["coach_pro", "coach_premium"]} title="Pro Feature">
 *   <ProContent />
 * </Paywall>
 */
const Paywall = ({
  children,
  requiredEntitlement,
  requiredEntitlements = [],
  title = 'Premium feature',
  description = 'Upgrade to unlock this feature',
  buttonText = 'Upgrade',
  showPreview = true,
  showLockIcon = true,
}) => {
  const entitlement = useSelector((state) => state.entitlement);
  // The loaded account's roles; the sign-in response's user until it loads.
  const permissions = useSelector((state) => state.user?.user?.permissions || state.auth?.user?.permissions || NO_PERMISSIONS);
  const navigate = useNavigate();
  const upgradePath = upgradePathFor(permissions);

  const allowedEntitlements = requiredEntitlement ? [requiredEntitlement, ...requiredEntitlements] : requiredEntitlements;
  const hasAccess = allowedEntitlements.includes(entitlement.entitlement) || entitlement.entitlement === 'admin';

  if (hasAccess) {
    return <>{children}</>;
  }

  const featureName = title.replace(/^Unlock (the )?/i, '');
  const subtitle = description
    .replace('Upgrade to view your ', '')
    .replace('Upgrade to view ', '')
    .replace("Upgrade to Athlete Premium to view this athlete's ", '')
    .replace(/\.$/, '');

  // Compact locked state (no preview): a centred card.
  if (!showPreview) {
    return (
      <div className="bm-root flex min-h-[300px] items-center justify-center rounded-[12px] border border-dashed border-bm-mutedbg bg-bm-subtle">
        <div className="flex max-w-md flex-col items-center gap-3 p-8 text-center">
          {showLockIcon && (
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bm-mutedbg text-bm-orange">
              <LockIcon />
            </span>
          )}
          <h3 className="bm-h-m text-bm-ink">{title}</h3>
          <p className="bm-body-m text-bm-muted">{description}</p>
          {upgradePath ? (
            <Btn onClick={() => navigate(upgradePath)}>{buttonText}</Btn>
          ) : (
            <PremiumNote className="justify-center bg-white" />
          )}
        </div>
      </div>
    );
  }

  // Locked card matching the V2 profile cards it sits among.
  return (
    <div className="bm-root flex h-full flex-col justify-between gap-4 rounded-[12px] border border-bm-border bg-white px-6 py-7">
      <div className="flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <Overline brand>{featureName}</Overline>
          <Badge tone="soft">
            <span className="mr-1 inline-flex text-bm-orange">
              <LockIcon />
            </span>
            Premium
          </Badge>
        </div>
        <p className="bm-body-m text-bm-muted">{subtitle ? subtitle.charAt(0).toUpperCase() + subtitle.slice(1) : description}</p>
      </div>
      {upgradePath ? (
        <Btn onClick={() => navigate(upgradePath)} className="w-full">
          {buttonText}
        </Btn>
      ) : (
        <PremiumNote />
      )}
    </div>
  );
};

export default Paywall;
